import { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { MagnifyingGlass, Funnel, Sparkle, ArrowRight, CaretDown, CaretUp, Buildings, SealCheck, Trophy, Star, Heart, List, SquaresFour, ArrowsLeftRight, CircleNotch, Info, X } from "@phosphor-icons/react";
import { predictColleges, getAIRecommendation, getFilterOptions, getSiteConfig, getMccQuotaOptions, CATEGORY_COLORS, CHANCE_STYLES, formatFees, formatRound } from "../lib/api";
import { CollegeLogo } from "../components/CollegeLogo";
import { getHighlight } from "../lib/highlightConfig";
import { getTextureCard } from "../lib/textures";
import { useInrToggle, initShowInrDefault } from "../lib/inrToggle";
import { useSeo } from "../components/plans/PlanShared";

const CURRENT_YEAR = new Date().getFullYear();

// Set <link rel="canonical"> to the given path (evergreen SEO — all year-aliased
// URLs like /ug-neet-2026/predict point back to the canonical /ug-neet/predict).
function useCanonical(path) {
  useEffect(() => {
    let tag = document.querySelector('link[rel="canonical"]');
    const created = !tag;
    if (!tag) {
      tag = document.createElement("link");
      tag.setAttribute("rel", "canonical");
      document.head.appendChild(tag);
    }
    const prev = tag.getAttribute("href");
    tag.setAttribute("href", `${window.location.origin}${path}`);
    return () => {
      if (created) tag.remove();
      else if (prev) tag.setAttribute("href", prev);
    };
  }, [path]);
}

// Chance badge — HIGH plays a one-time mini sparkle that settles, then the badge reveals.
// Optimistic HIGH (candidate just missed an earlier round by <=0.1%) gets an "optimistic" tag.
function ChanceBadge({ chance, optimistic, sizeClass, testid }) {
  const base = `inline-block px-2 py-0.5 ${sizeClass} font-bold uppercase tracking-wider border ${CHANCE_STYLES[chance]}`;
  if (chance !== "high") {
    return <span data-testid={testid} className={base}>{chance}</span>;
  }
  const badge = (
    <span className="chance-high-wrap">
      <span className={`chance-high-shine ${base}`}>high</span>
      <Sparkle weight="fill" className="chance-spark chance-spark-1" />
      <Sparkle weight="fill" className="chance-spark chance-spark-2" />
      <Sparkle weight="fill" className="chance-spark chance-spark-3" />
    </span>
  );
  if (!optimistic) {
    return <span data-testid={testid}>{badge}</span>;
  }
  return (
    <span className="inline-flex flex-col items-center gap-0.5" data-testid={testid}
      title="Optimistic: your rank just missed this round's cutoff by ≤0.1% — shown as a likely admit if the cutoff holds steady.">
      {badge}
      <span className="text-[9px] font-bold uppercase tracking-wide text-[#00C04B]/70 leading-none" data-testid={`${testid}-optimistic`}>optimistic</span>
    </span>
  );
}

// 2-decimal fee formatter for predictor rows, trailing zeros trimmed (₹49.09L, ₹24.5K, ₹50).
const formatFees2 = (v) => {
  if (v == null) return "";
  const trim = (n) => `${parseFloat(n.toFixed(2))}`;
  if (v >= 100000) return `${trim(v / 100000)}L`;
  if (v >= 1000) return `${trim(v / 1000)}K`;
  return `${v.toLocaleString()}`;
};

const FEE_TYPE_STYLES = {
  Govt: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Regular: "bg-indigo-50 text-indigo-700 border-indigo-200",
  NRI: "bg-amber-50 text-amber-700 border-amber-200",
};

// Contextual per-row fee: currency symbol + amount + type tag (Govt/Regular/NRI).
// USD fees can be shown converted to INR via a site-wide toggle (button appears only for USD).
export const FeeDisplay = ({ p, align = "end", tagSizeClass = "text-[9px]" }) => {
  const [showInr, setShowInr] = useInrToggle();
  const isUsd = p.fee_currency === "USD";
  const inrMode = isUsd && showInr;
  const amount = inrMode ? (p.fees_inr ?? p.fees) : (p.fees ?? p.annual_fees);
  const sym = inrMode || !isUsd ? "₹" : "$";
  return (
    <span className={`inline-flex flex-col items-${align} gap-0.5`} data-testid="predictor-fee">
      <span>{amount || amount === 0 ? `${sym}${formatFees2(amount)}` : "N/A"}</span>
      <span className="inline-flex items-center gap-1.5">
        {isUsd && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowInr(!showInr); }}
            title="Toggle USD / INR (applies site-wide)"
            className="inline-flex items-center gap-0.5 text-[9px] font-bold leading-none px-1.5 py-1 border border-zinc-300 text-zinc-600 hover:border-[#6043EF] hover:text-[#6043EF] transition-colors rounded"
            data-testid="fee-currency-toggle"
          >
            <ArrowsLeftRight size={9} weight="bold" />{inrMode ? "$" : "₹"}
          </button>
        )}
      </span>
    </span>
  );
};


// Bump to invalidate stale cached predictor results in localStorage (e.g. after a display-name change).
// v8: earliest-admitting-round redesign changed the meaning of round_number/closing_rank.
// v9: chance-label bands retuned (HIGH=clears round, MEDIUM<=2% miss, LOW<=5% miss).
// v10: default year filter now selects ALL available years (was latest-only 2026 which hid state bodies).
// v11: earliest-round walk got a 0.1% optimistic grace (barely-missed earlier round now counts).
// v12: /predict now returns `optimistic` flag; HIGH badges show an "optimistic" tag for grace picks.
const RESULTS_CACHE_VERSION = 13;

// Static adjacency map for Indian states
const STATE_ADJACENCY = {
  "Karnataka": ["Maharashtra", "Goa", "Kerala", "Tamil Nadu", "Andhra Pradesh", "Telangana"],
  "Maharashtra": ["Gujarat", "Madhya Pradesh", "Chhattisgarh", "Karnataka", "Goa", "Telangana"],
  "Tamil Nadu": ["Kerala", "Karnataka", "Andhra Pradesh", "Puducherry"],
  "Kerala": ["Tamil Nadu", "Karnataka"],
  "Andhra Pradesh": ["Telangana", "Karnataka", "Tamil Nadu", "Odisha", "Chhattisgarh"],
  "Telangana": ["Maharashtra", "Karnataka", "Andhra Pradesh", "Chhattisgarh"],
  "Gujarat": ["Maharashtra", "Madhya Pradesh", "Rajasthan"],
  "Rajasthan": ["Gujarat", "Madhya Pradesh", "Uttar Pradesh", "Haryana", "Punjab"],
  "Uttar Pradesh": ["Madhya Pradesh", "Rajasthan", "Haryana", "Delhi", "Uttarakhand", "Bihar", "Jharkhand"],
  "Bihar": ["Uttar Pradesh", "Jharkhand", "West Bengal"],
  "West Bengal": ["Bihar", "Jharkhand", "Odisha", "Assam"],
  "Madhya Pradesh": ["Uttar Pradesh", "Rajasthan", "Gujarat", "Maharashtra", "Chhattisgarh"],
  "Punjab": ["Haryana", "Rajasthan", "Himachal Pradesh", "Jammu & Kashmir"],
  "Haryana": ["Punjab", "Rajasthan", "Uttar Pradesh", "Delhi", "Himachal Pradesh"],
  "Delhi": ["Haryana", "Uttar Pradesh"],
  "Jharkhand": ["Bihar", "West Bengal", "Odisha", "Uttar Pradesh"],
  "Odisha": ["West Bengal", "Jharkhand", "Andhra Pradesh"],
  "Uttarakhand": ["Uttar Pradesh", "Himachal Pradesh"],
  "Himachal Pradesh": ["Punjab", "Haryana", "Uttarakhand", "Jammu & Kashmir"],
  "Assam": ["West Bengal"],
  "Jammu & Kashmir": ["Punjab", "Himachal Pradesh"],
  "Puducherry": ["Tamil Nadu"],
  "Goa": ["Maharashtra", "Karnataka"],
  "Chhattisgarh": ["Madhya Pradesh", "Maharashtra", "Telangana", "Andhra Pradesh", "Odisha", "Jharkhand"],
};

// Popular states by medical college density
const STATE_POPULARITY = [
  "Delhi", "Karnataka", "Maharashtra", "Tamil Nadu", "Uttar Pradesh",
  "Kerala", "Telangana", "West Bengal", "Rajasthan", "Bihar",
  "Madhya Pradesh", "Gujarat", "Punjab", "Andhra Pradesh", "Jharkhand",
  "Odisha", "Haryana", "Himachal Pradesh", "Uttarakhand", "Puducherry",
  "Assam", "Jammu & Kashmir", "Goa", "Chhattisgarh"
];

function getStateOrder(domicileState) {
  if (!domicileState || domicileState === "All India") return STATE_POPULARITY;
  const adjacent = STATE_ADJACENCY[domicileState] || [];
  const rest = STATE_POPULARITY.filter(s => s !== domicileState && !adjacent.includes(s));
  return [domicileState, ...adjacent, ...rest];
}

// Domicile state -> the counselling bodies we hold for that state (one-to-one mostly)
const STATE_BODY_MAP = {
  "Andhra Pradesh": ["Andhra_Pradesh"],
  "Telangana": ["Telangana"],
  "Karnataka": ["Karnataka"],
  "Maharashtra": ["Maharashtra"],
  "Kerala": ["Kerala"],
  "Bihar": ["Bihar"],
  "Uttar Pradesh": ["Uttar_Pradesh"],
  "Rajasthan": ["Rajasthan"],
  "Jharkhand": ["Jharkhand"],
  "Haryana": ["Haryana"],
  "West Bengal": ["West_Bengal"],
  "Puducherry": ["Puducherry"],
  "Madhya Pradesh": ["Madhya_Pradesh"],
};

// Bodies of "open states" whose private/management seats admit non-domicile students.
// (AP/Telangana: only their _Mgmt Category-B bodies; govt bodies stay domicile-only.)
const OPEN_STATE_MGMT_BODIES = [
  "Karnataka", "Kerala", "Uttar_Pradesh", "Bihar", "West_Bengal", "Haryana",
  "Andhra_Pradesh", "Telangana",
];

// For NON-DOMICILE candidates, open states admit you only to the GENERAL / OPEN /
// MANAGEMENT pool — home-state caste reservation (OBC/SC/ST/EWS) does NOT apply.
// These are the open/management category codes per body in our data.
const OPEN_STATE_MGMT_CATEGORY = {
  "Karnataka": ["OPN", "NRI", "OTH"], // Open/private + NRI + Other-state mgmt (non-domicile pool)
  "Kerala": ["SM"],               // State/open merit
  "Uttar_Pradesh": ["UROP"],      // Unreserved Open
  "Bihar": ["UR"],                // Unreserved (management merit)
  "West_Bengal": ["MGMT_UR", "NRI_UR"], // Management + NRI unreserved (non-domicile pool)
  "Haryana": ["OPEN_CAT", "MGT"], // Open + Management
  "Andhra_Pradesh": ["CAT_B1_NL", "CAT_C_NRI"], // Non-local Cat-B1 + Category-C/NRI (non-domicile pool)
  "Telangana": ["UR_NL", "NRI", "NRI_MIN"], // Non-local UR + NRI (non-domicile mgmt pool)
};

// Per-domicile default categories to pre-select for the HOME state body.
// (Maharashtra: Open + Open(women) + NRI + Institutional Quota.)
const STATE_DEFAULT_CATEGORIES = {
  "Maharashtra": ["OPEN", "OPEN_W", "NRI", "IQ"],
  "West Bengal": ["UR", "MGMT_UR", "NRI_UR"],
  "Telangana": ["UR"],
  "Bihar": ["UR"],
};

// The OPEN / GENERAL (unreserved) category code for each counselling body.
// Used as the default pick for a body when the user hasn't chosen a category.
// (MCC: Open; Karnataka: General Merit; Kerala: State Merit; etc.)
const OPEN_CATEGORY_BY_BODY = {
  MCC: ["OP"],
  Bihar: ["UR"],
  Jharkhand: ["UR"],
  West_Bengal: ["UR"],
  Uttar_Pradesh: ["UROP"],
  Karnataka: ["GM"],
  Kerala: ["SM"],
  Maharashtra: ["OPEN"],
  Haryana: ["OPEN_CAT"],
  Telangana: ["UR"],
  Andhra_Pradesh: ["CAT_B1_NL", "CAT_C_NRI"],
  Madhya_Pradesh: ["GQ_UR_X_OP"],
  Sikkim: ["GENERAL"],
  // Rajasthan: pending (gender-split GEN_URB/GEN_URG scheme)
};

// Effective counselling bodies for a domicile selection.
// Default: MCC (AIQ) + the state's own body. With mgmt-on: also add the open states'
// management bodies (backend further restricts these to non-govt seats only).
function computeDomicileBodies(state, mgmt, allBodies, openBodies = OPEN_STATE_MGMT_BODIES) {
  if (!state || state === "All India") return null;
  const stateBodies = STATE_BODY_MAP[state] || [];
  let want = ["MCC", ...stateBodies];
  if (mgmt) want = [...want, ...openBodies];
  const valid = want.filter((b) => allBodies.includes(b));
  return valid.length ? [...new Set(valid)] : ["MCC"];
}

// College-type badge colors (mirrors CollegeDetail historical cutoffs styling)
const TYPE_BADGE = {
  govt: "bg-[#00C04B]/10 text-[#00C04B] border-[#00C04B]",
  private: "bg-gradient-to-b from-[#FFE082] to-[#E8A200] text-[#5C3B00] border-[#E0A000]",
  trust: "bg-[#92700C]/10 text-[#92700C] border-[#92700C]",
  deemed: "bg-[#6043EF]/10 text-[#6043EF] border-[#6043EF]",
};

// Fee-bucket ordering for the category dropdown — NRI, Other, Private first; Govt last.
const FEE_BUCKET_ORDER = { nri: 0, other: 1, private: 2, govt: 3 };
const FEE_BUCKET_META = {
  nri:     { label: "NRI",     text: "text-rose-600",    dot: "bg-rose-500" },
  other:   { label: "Other",   text: "text-amber-600",   dot: "bg-amber-500" },
  private: { label: "Private", text: "text-violet-600",  dot: "bg-violet-500" },
  govt:    { label: "Govt",    text: "text-emerald-600", dot: "bg-emerald-500" },
};


// Distinct chip shade per counselling round (stray rounds share a red tone)
const ROUND_CHIP = {
  1: "bg-[#0284C7]/10 text-[#0369A1]",
  2: "bg-[#EAB308]/15 text-[#A16207]",
  3: "bg-[#EA580C]/10 text-[#C2410C]",
  4: "bg-[#00A37A]/10 text-[#047857]",
  5: "bg-[#DB2777]/10 text-[#BE185D]",
  6: "bg-[#7C3AED]/10 text-[#6D28D9]",
  7: "bg-[#0891B2]/10 text-[#0E7490]",
};
function roundChip(rn) {
  if (rn >= 100) return "bg-[#DC2626]/10 text-[#B91C1C]";
  return ROUND_CHIP[rn] || "bg-zinc-100 text-zinc-500";
}

// Distinct chip shade per historical year
const YEAR_CHIP = {
  2025: "bg-[#6043EF]/12 text-[#4A28C2]",
  2024: "bg-[#0284C7]/12 text-[#0369A1]",
  2023: "bg-[#00A37A]/12 text-[#047857]",
  2022: "bg-[#C2410C]/12 text-[#9A3412]",
  2021: "bg-[#DB2777]/12 text-[#BE185D]",
};
function yearChip(y) {
  return YEAR_CHIP[y] || "bg-zinc-100 text-zinc-500";
}

function fontClasses(lg) {
  return {
    big: lg ? "text-[17px]" : "text-sm",
    md: lg ? "text-[12px]" : "text-[10px]",
    xs2: lg ? "text-xs" : "text-[10px]",
    sm2: lg ? "text-sm" : "text-xs",
    tiny: lg ? "text-[10px]" : "text-[8px]",
    s9: lg ? "text-[11px]" : "text-[9px]",
    cityCard: lg ? "text-[13px]" : "text-[11px]",
    gt: lg ? "text-sm" : "text-xs",
  };
}

export default function CollegePredictor() {
  const [searchParams] = useSearchParams();
  useSeo(
    `UG-NEET ${CURRENT_YEAR} College Predictor — Predict Medical Colleges by Rank`,
    `Free UG-NEET ${CURRENT_YEAR} college predictor. Enter your NEET rank, category and state to see the medical/dental colleges you can get across MCC & state counselling.`
  );
  useCanonical("/ug-neet/predict");
  
  // Load saved filters from localStorage
  const savedFilters = (() => {
    try {
      const s = localStorage.getItem("predictor_filters");
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  })();

  const [rank, setRank] = useState(searchParams.get("rank") || savedFilters?.rank || "");
  const [assumeStateRank, setAssumeStateRank] = useState(savedFilters?.assumeStateRank || false);
  const [selectedCategories, setSelectedCategories] = useState(() => {
    const urlCat = searchParams.get("category");
    return urlCat ? [urlCat] : (savedFilters?.categories || ["OP"]);
  });
  const [selectedBodies, setSelectedBodies] = useState(savedFilters?.bodies || ["MCC"]);
  const [collegeType, setCollegeType] = useState(savedFilters?.collegeType || "all");
  const [nationalImportanceOnly, setNationalImportanceOnly] = useState(savedFilters?.nationalImportanceOnly || false);
  const [course, setCourse] = useState(savedFilters?.course || "MBBS");
  const [maxFees, setMaxFees] = useState(savedFilters?.maxFees || [5000000]);
  const [roundNumber, setRoundNumber] = useState(savedFilters?.roundNumber || "");
  const [filterState, setFilterState] = useState(savedFilters?.filterState || "All");
  const [domicileState, setDomicileState] = useState(searchParams.get("domicile") || savedFilters?.domicileState || "All India");
  const [includeOtherMgmt, setIncludeOtherMgmt] = useState(savedFilters?.includeOtherMgmt || false);
  const [selectedYears, setSelectedYears] = useState(savedFilters?.years || []);
  const [availableYears, setAvailableYears] = useState([]);
  const savedResults = (() => {
    try {
      const s = localStorage.getItem("predictor_results");
      const parsed = s ? JSON.parse(s) : null;
      // Invalidate caches from before the display-name change (NMC alias names).
      return parsed && parsed.v === RESULTS_CACHE_VERSION ? parsed : null;
    } catch { return null; }
  })();
  const [predictions, setPredictions] = useState(savedResults?.predictions || []);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState("");
  const [filterOptions, setFilterOptions] = useState({ states: [], categories: [], counselling_bodies: [] });
  const [mccQuotaData, setMccQuotaData] = useState({ all: [], universal: [], domicile_auto: {} });
  const [mccQuotaSel, setMccQuotaSel] = useState([]);   // selected opt-in quota names
  const [mccQuotaOpen, setMccQuotaOpen] = useState(false);
  const [sortField, setSortField] = useState("chance");
  const [sortDir, setSortDir] = useState("asc");
  const [total, setTotal] = useState(savedResults?.total || 0);
  const [searched, setSearched] = useState(savedResults?.searched || false);

  // Client-side search within results (browser-only, no DB hit)
  const [resultSearch, setResultSearch] = useState("");
  // Results view mode: table (rows) or cards (grid) — persisted, URL-overridable
  const [viewMode, setViewMode] = useState(() => {
    const urlView = searchParams.get("view");
    if (urlView === "cards" || urlView === "table") return urlView;
    try { return localStorage.getItem("predictor_view") || "table"; } catch { return "table"; }
  });
  useEffect(() => {
    try { localStorage.setItem("predictor_view", viewMode); } catch { /* ignore */ }
  }, [viewMode]);
  // Font scale: false = normal (default), true = larger (+20%)
  const [fontLg, setFontLg] = useState(() => {
    try { return localStorage.getItem("predictor_font") === "lg"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("predictor_font", fontLg ? "lg" : "sm"); } catch { /* ignore */ }
  }, [fontLg]);
  // Favorites — persisted in localStorage (keyed by college_id)
  const [favoriteColleges, setFavoriteColleges] = useState(() => {
    try {
      const s = localStorage.getItem("predictor_favorites");
      return s ? JSON.parse(s) : [];
    } catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem("predictor_favorites", JSON.stringify(favoriteColleges));
  }, [favoriteColleges]);
  const toggleFavorite = useCallback((collegeId) => {
    setFavoriteColleges(prev =>
      prev.includes(collegeId) ? prev.filter(x => x !== collegeId) : [...prev, collegeId]);
  }, []);

  // College-card texture from Admin → Config
  const [cardTexture, setCardTexture] = useState(null);
  const [catsExpanded, setCatsExpanded] = useState(false);
  useEffect(() => {
    getSiteConfig().then((res) => {
      const h = getHighlight(res.data.page_highlights || {}, "predictor", "college_card");
      setCardTexture(h ? getTextureCard(h.texture_id) : null);
      initShowInrDefault(!!res.data.show_fees_in_inr);
      // Server-controlled cache bust: admin bumps this in UI Config after surgical
      // data edits to force ALL returning users to drop their cached results.
      try {
        const serverBust = String(res.data.predictor_cache_bust ?? 0);
        if (localStorage.getItem("predictor_cache_bust") !== serverBust) {
          localStorage.setItem("predictor_cache_bust", serverBust);
          localStorage.removeItem("predictor_results");
          setPredictions([]); setSearched(false); setTotal(0); setAiRecommendation("");
        }
      } catch { /* ignore */ }
    }).catch(() => {});
  }, []);

  const [categoriesReady, setCategoriesReady] = useState(false);
  const [categoriesGrouped, setCategoriesGrouped] = useState({});
  const [categoryBuckets, setCategoryBuckets] = useState({});
  const [coursesGrouped, setCoursesGrouped] = useState({});

  const [allBodiesGrouped, setAllBodiesGrouped] = useState({});

  // Open-state category mapping (DB-backed, from /api/filter-options) — the subset
  // of each open-state body's categories a non-domicile candidate can access.
  // Falls back to the historical hardcoded constant until filter-options loads.
  const openStateMap = filterOptions.open_state_mapping || OPEN_STATE_MGMT_CATEGORY;
  const openStateBodies = useMemo(() => Object.keys(openStateMap), [openStateMap]);

  // Dismissible info callout shown when the open-states checkbox is on.
  const [openInfoDismissed, setOpenInfoDismissed] = useState(() => {
    try { return localStorage.getItem("open_state_info_dismissed") === "1"; } catch { return false; }
  });

  // Persist filters to localStorage on change
  useEffect(() => {
    const filters = { rank, assumeStateRank, categories: selectedCategories, bodies: selectedBodies, collegeType, nationalImportanceOnly, course, maxFees, roundNumber, filterState, domicileState, includeOtherMgmt, years: selectedYears };
    localStorage.setItem("predictor_filters", JSON.stringify(filters));
  }, [rank, assumeStateRank, selectedCategories, selectedBodies, collegeType, nationalImportanceOnly, course, maxFees, roundNumber, filterState, domicileState, includeOtherMgmt, selectedYears]);

  // Load all available bodies on mount
  useEffect(() => {
    getFilterOptions().then((res) => {
      setFilterOptions(res.data);
      setCoursesGrouped(res.data.courses_grouped || {});
      // Fetch all bodies' categories for the checkbox list
      const allBodies = (res.data.counselling_bodies || []).join(",");
      if (allBodies) {
        getFilterOptions({ counselling_body: allBodies }).then((r2) => {
          setAllBodiesGrouped(r2.data.categories_grouped || {});
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    getMccQuotaOptions().then((res) => setMccQuotaData(res.data || { all: [], universal: [], domicile_auto: {} })).catch(() => {});
  }, []);

  // Quotas auto-applied by domicile (non-selectable info) vs. personal opt-ins (selectable).
  // B.Sc Nursing quotas appear only when the Nursing course is selected (and vice-versa).
  const mccAutoInfo = useMemo(() => {
    const nursingSet = mccQuotaData.nursing || [];
    const isNursing = course === "BSc_Nursing";
    const courseOk = (q) => (isNursing ? nursingSet.includes(q) : !nursingSet.includes(q));
    const univ = mccQuotaData.universal || [];
    const da = mccQuotaData.domicile_auto || {};
    return [
      ...univ.map((q) => ({ q, tag: "Any domicile", active: true })),
      ...(da.Delhi || []).map((q) => ({ q, tag: "Delhi domicile", active: domicileState === "Delhi" })),
      ...(da.Puducherry || []).map((q) => ({ q, tag: "Puducherry domicile", active: domicileState === "Puducherry" })),
    ].filter((it) => courseOk(it.q));
  }, [mccQuotaData, domicileState, course]);

  const mccSelectableQuotas = useMemo(() => {
    const nursingSet = mccQuotaData.nursing || [];
    const isNursing = course === "BSc_Nursing";
    const courseOk = (q) => (isNursing ? nursingSet.includes(q) : !nursingSet.includes(q));
    const autoSet = new Set([
      ...(mccQuotaData.universal || []),
      ...((mccQuotaData.domicile_auto || {}).Delhi || []),
      ...((mccQuotaData.domicile_auto || {}).Puducherry || []),
    ]);
    return (mccQuotaData.all || []).filter((q) => !autoSet.has(q) && courseOk(q));
  }, [mccQuotaData, course]);

  // Personal opt-in quota names sent to the predictor.
  const mccExtraQuotas = useMemo(() => mccQuotaSel, [mccQuotaSel]);

  // Pre-select ALL personal-eligibility quotas by default so their matching seats
  // surface in results (highlighted for eyeball verification). Re-seeds when the
  // selectable set changes (data load / course switch); in-course deselections persist.
  useEffect(() => {
    setMccQuotaSel(mccSelectableQuotas);
  }, [mccSelectableQuotas.join(",")]); // eslint-disable-line

  // When selected bodies change, reload grouped categories for dropdown.
  // Debounced (300ms) so rapid multi-body toggling fires only one network call.
  useEffect(() => {
    const bodyParam = selectedBodies.length > 0 ? selectedBodies.join(",") : "MCC";
    setCategoriesReady(false);
    const t = setTimeout(() => {
      getFilterOptions({ counselling_body: bodyParam }).then((res) => {
      const grouped = res.data.categories_grouped || {};
      const allCats = res.data.categories || [];
      setCategoriesGrouped(grouped);
      setCategoryBuckets(res.data.category_buckets || {});
      // Update courses_grouped with fresh data for selected bodies
      if (res.data.courses_grouped) {
        setCoursesGrouped(prev => ({ ...prev, ...res.data.courses_grouped }));
      }
      // Ensure at least one category per selected body is included
      setSelectedCategories(prev => {
        const valid = prev.filter(c => allCats.includes(c));
        // Check which bodies already have a category selected
        const bodiesWithCat = new Set();
        for (const body of selectedBodies) {
          const bodyCats = grouped[body] || [];
          if (valid.some(c => bodyCats.includes(c))) {
            bodiesWithCat.add(body);
          }
        }
        // Add a default for bodies without any selected category
        const additions = [];
        const homeBodies = STATE_BODY_MAP[domicileState] || [];
        for (const body of selectedBodies) {
          const bodyCats = grouped[body] || [];
          const isOpenNonHome = domicileState !== "All India" && includeOtherMgmt && !homeBodies.includes(body);
          if (isOpenNonHome) {
            // Open state for non-domicile: ALWAYS ensure the open/management set is selected
            // (don't rely on the shared-name "bodiesWithCat" check — e.g. NRI exists in many bodies)
            const openCats = (openStateMap[body] || []).filter(c => bodyCats.includes(c));
            additions.push(...openCats);
          } else if (!bodiesWithCat.has(body)) {
            const stateDefaults = (homeBodies.includes(body) && STATE_DEFAULT_CATEGORIES[domicileState])
              ? STATE_DEFAULT_CATEGORIES[domicileState].filter(c => bodyCats.includes(c))
              : [];
            const openDefaults = (OPEN_CATEGORY_BY_BODY[body] || []).filter(c => bodyCats.includes(c));
            if (stateDefaults.length > 0) additions.push(...stateDefaults);
            else if (openDefaults.length > 0) additions.push(...openDefaults);
            else if (bodyCats.includes("OP")) additions.push("OP");
            else if (bodyCats.includes("GM")) additions.push("GM");
            else if (bodyCats.length > 0) additions.push(bodyCats[0]);
          }
        }
        const merged = [...new Set([...valid, ...additions])];
        if (merged.length > 0) return merged;
        // Fallback: prefer the open/general category of a selected body, else first available.
        const fb = selectedBodies
          .flatMap(b => (OPEN_CATEGORY_BY_BODY[b] || []))
          .find(c => allCats.includes(c));
        return fb ? [fb] : (allCats.length > 0 ? [allCats[0]] : []);
      });
      setCategoriesReady(true);
      }).catch(() => setCategoriesReady(true));
    }, 300);
    return () => clearTimeout(t);
  }, [selectedBodies.join(",")]); // eslint-disable-line

  // Auto-predict from URL params — wait for categories to load first
  useEffect(() => {
    if (categoriesReady && searchParams.get("rank") && !searched) {
      handlePredict();
    }
  }, [categoriesReady]); // eslint-disable-line

  const toggleYear = (yr) => {
    setSelectedYears(prev => {
      const next = prev.includes(yr) ? prev.filter(y => y !== yr) : [...prev, yr];
      // Auto-repredict after toggling
      setTimeout(() => { document.querySelector('[data-testid="predict-btn"]')?.click(); }, 100);
      return next;
    });
  };

  // Per-body category intent — shared by the predict payload AND the selected
  // chips, so a flat shared code (e.g. "UR" in Bihar/WB) isn't mis-attributed to
  // every body. Open-state (non-domicile) bodies use their forced OPEN/MGMT pool.
  const bodyCategoriesMap = useMemo(() => {
    const map = {};
    selectedCategories.forEach((cat) => {
      // Only attribute a category to a body the user actually has selected — prevents
      // stale/fallback categoriesGrouped (e.g. MCC) from showing "MCC: BC" after MCC is unchecked.
      const body = Object.entries(categoriesGrouped)
        .find(([bd, cats]) => selectedBodies.includes(bd) && cats.includes(cat))?.[0];
      if (body) {
        if (!map[body]) map[body] = [];
        map[body].push(cat);
      }
    });
    if (domicileState !== "All India" && includeOtherMgmt) {
      const homeBodies = STATE_BODY_MAP[domicileState] || [];
      Object.entries(openStateMap).forEach(([body, cats]) => {
        if (!homeBodies.includes(body) && selectedBodies.includes(body)) {
          // Respect the user's per-chip selections: keep only forced open/mgmt
          // categories that are still selected, so removing a chip (or unchecking
          // it in the dropdown) actually drops it from the chips AND the payload.
          const kept = cats.filter((c) => selectedCategories.includes(c));
          if (kept.length > 0) map[body] = kept;
          else delete map[body];
        }
      });
    }
    return map;
  }, [selectedCategories, categoriesGrouped, domicileState, includeOtherMgmt, selectedBodies, openStateMap]);

  // Effective category selection = home body (all selected) + open-state bodies
  // (only their included subset). Drives the "N selected" count + Select-all list.
  const effectiveSelectedCount = useMemo(
    () => Object.values(bodyCategoriesMap).reduce((n, cs) => n + cs.length, 0),
    [bodyCategoriesMap]);

  // categoriesGrouped with open-state (non-home) bodies restricted to their
  // Included categories only — used by the dropdown + Select-all when open-states on.
  const effectiveGrouped = useMemo(() => {
    if (domicileState === "All India" || !includeOtherMgmt) return categoriesGrouped;
    const homeBodies = STATE_BODY_MAP[domicileState] || [];
    const out = {};
    Object.entries(categoriesGrouped).forEach(([body, cats]) => {
      const isOpenNonHome = !homeBodies.includes(body) && openStateMap[body];
      const kept = isOpenNonHome ? cats.filter(c => openStateMap[body].includes(c)) : cats;
      if (kept.length > 0) out[body] = kept;
    });
    return out;
  }, [categoriesGrouped, domicileState, includeOtherMgmt, openStateMap]);

  const handlePredict = useCallback(async () => {
    if (!rank) return;
    setLoading(true);
    setSearched(true);
    setAiRecommendation("");
    try {
      const bodyCategories = bodyCategoriesMap;
      const res = await predictColleges({
        rank: parseInt(rank),
        categories: selectedCategories,
        body_categories: bodyCategories,
        course,
        counselling_bodies: selectedBodies,
        preferred_college_type: collegeType,
        national_importance_only: nationalImportanceOnly,
        max_fees: maxFees[0] < 5000000 ? maxFees[0] : null,
        round_number: roundNumber && roundNumber !== "all" ? parseInt(roundNumber) : null,
        years: selectedYears,
        filter_state: filterState !== "All" ? filterState : null,
        domicile_state: domicileState,
        include_other_mgmt: includeOtherMgmt,
        assume_state_rank: assumeStateRank,
        mcc_quotas: mccExtraQuotas,
      });
      const preds = res.data.predictions || [];
      setPredictions(preds);
      setTotal(res.data.total || 0);
      try { window.dispatchEvent(new Event("emmy-search")); } catch { /* noop */ }
      try {
        localStorage.setItem("predictor_results", JSON.stringify({
          predictions: res.data.predictions || [],
          total: res.data.total || 0,
          searched: true,
          v: RESULTS_CACHE_VERSION,
        }));
      } catch { /* ignore quota errors */ }
      if (res.data.available_years && availableYears.length === 0) {
        setAvailableYears(res.data.available_years);
        if (selectedYears.length === 0 && res.data.available_years.length > 0) {
          // Select ALL available years by default. Picking only the latest single year
          // (e.g. 2026) hid every body whose newest data is an earlier year — most state
          // bodies top out at 2025 — yielding "no colleges found". With all years selected,
          // each college shows its own latest available year plus the full range band.
          setSelectedYears(res.data.available_years);
        }
      }
    } catch (e) {
      console.error("Prediction failed", e);
    }
    setLoading(false);
  }, [rank, assumeStateRank, selectedCategories, course, collegeType, nationalImportanceOnly, maxFees, roundNumber, selectedYears, filterState, domicileState, includeOtherMgmt, selectedBodies.join(","), categoriesGrouped, bodyCategoriesMap, mccExtraQuotas.join(",")]); // eslint-disable-line

  // Re-run prediction when opt-in MCC quotas change (only if already searched).
  useEffect(() => {
    if (searched && rank) handlePredict();
  }, [mccExtraQuotas.join(",")]); // eslint-disable-line

  const handleAIRecommend = async () => {
    if (!rank) return;
    setAiLoading(true);
    try {
      const bodyCategories = bodyCategoriesMap;
      const res = await getAIRecommendation({
        rank: parseInt(rank), categories: selectedCategories, body_categories: bodyCategories,
        counselling_bodies: selectedBodies,
        preferred_college_type: collegeType,
        national_importance_only: nationalImportanceOnly,
        max_fees: maxFees[0] < 5000000 ? maxFees[0] : null,
        years: selectedYears,
      });
      setAiRecommendation(res.data.recommendation || "");
    } catch (e) { console.error("AI recommendation failed", e); }
    setAiLoading(false);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const sortedPredictions = [...predictions].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortField === "chance") {
      const order = { high: 0, medium: 1, low: 2 };
      return (order[a.chance] - order[b.chance]) * dir;
    }
    if (sortField === "rank") return (a.last_year_closing_rank - b.last_year_closing_rank) * dir;
    if (sortField === "fees") return (((a.fees_inr ?? a.fees ?? a.annual_fees) || 0) - ((b.fees_inr ?? b.fees ?? b.annual_fees) || 0)) * dir;
    if (sortField === "name") return a.college_name.localeCompare(b.college_name) * dir;
    return 0;
  });

  // Deduplicate (college × category): when the same college shows multiple
  // closing ranks for the SAME category (multiple rounds/years/quotas), keep
  // ONE row — the closing rank NEAREST to the user's rank. Different
  // categories for the same college remain as separate rows.
  const userRankNum = Number(rank) || 0;
  const dedupedPredictions = (() => {
    if (!userRankNum) return sortedPredictions;
    const bestByKey = new Map();
    for (const p of sortedPredictions) {
      const key = `${p.college_code || p.college_id || p.college_name}|${p.category || ""}`;
      const existing = bestByKey.get(key);
      const dist = Math.abs((p.last_year_closing_rank || 0) - userRankNum);
      if (!existing || dist < existing.dist) {
        bestByKey.set(key, { row: p, dist });
      }
    }
    // Preserve original sort order among the kept rows.
    const kept = new Set([...bestByKey.values()].map((x) => x.row));
    return sortedPredictions.filter((p) => kept.has(p));
  })();

  // Client-side text filter over results (matches college name or city)
  const q = resultSearch.trim().toLowerCase();
  const filteredPredictions = q
    ? dedupedPredictions.filter(p =>
        (p.search_text || `${p.college_name || ""} ${p.city || ""}`).toLowerCase().includes(q))
    : dedupedPredictions;

  // Split favorites so they surface as a dedicated group on top
  const favSet = new Set(favoriteColleges);
  const favoriteRows = filteredPredictions.filter(p => favSet.has(p.college_id));
  const nonFavoriteRows = filteredPredictions.filter(p => !favSet.has(p.college_id));

  // Group by state with adjacency ordering (favorites excluded — shown separately)
  const stateOrder = getStateOrder(domicileState);
  const groupedByState = {};
  nonFavoriteRows.forEach(p => {
    if (!groupedByState[p.state]) groupedByState[p.state] = [];
    groupedByState[p.state].push(p);
  });
  const orderedStates = stateOrder.filter(s => groupedByState[s]);
  // Add any states not in the order map
  Object.keys(groupedByState).forEach(s => {
    if (!orderedStates.includes(s)) orderedStates.push(s);
  });

  // Unique states in results for filter
  const resultStates = [...new Set(predictions.map(p => p.state))].sort();

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <CaretDown size={10} className="text-zinc-400" />;
    return sortDir === "asc" ? <CaretUp size={10} className="text-[#6043EF]" /> : <CaretDown size={10} className="text-[#6043EF]" />;
  };

  let globalIdx = 0;
  const fs = fontClasses(fontLg);

  return (
    <div className="min-h-screen" data-testid="predictor-page">
      {/* Header */}
      <div className="border-b border-zinc-200 bg-[#F4F4F5]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-1">PREDICTION ENGINE</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-none text-[#09090B] font-heading">
            UG-NEET {CURRENT_YEAR} College Predictor
          </h1>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-12 gap-4">
          {/* Filters Panel */}
          <div className="col-span-12 lg:col-span-3 border border-zinc-200" data-testid="filters-panel">
            <div className="p-4 border-b border-zinc-200 bg-[#F4F4F5]">
              <div className="flex items-center gap-2">
                <Funnel size={14} weight="bold" className="text-[#6043EF]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">FILTERS</span>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Rank */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">
                  {assumeStateRank ? "STATE MERIT RANK" : "NEET AIR"}
                </label>
                <Input type="number" value={rank} onChange={(e) => setRank(e.target.value)}
                  placeholder={assumeStateRank ? "e.g. 50 (CML)" : "e.g. 5000"}
                  className="border-zinc-300 font-['IBM_Plex_Mono'] focus:border-[#6043EF]"
                  data-testid="filter-rank-input" min={1} />
                <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="assume-state-rank-checkbox">
                  <input
                    type="checkbox"
                    checked={assumeStateRank}
                    onChange={(e) => setAssumeStateRank(e.target.checked)}
                    className="w-3.5 h-3.5 accent-[#6043EF]"
                  />
                  <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#52525B]">
                    Assume State Rank (CML/KEAM)
                  </span>
                </label>
                {assumeStateRank && (
                  <p className="text-[10px] text-[#FFC107] mt-1 font-sans">
                    Results limited to bodies using state merit rank (Jharkhand, Kerala)
                  </p>
                )}
              </div>

              {/* Domicile State */}
              <div data-testid="domicile-section">
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">DOMICILE STATE</label>
                <Select value={domicileState} onValueChange={(v) => {
                  setDomicileState(v);
                  if (v === "All India") { setIncludeOtherMgmt(false); }
                  else {
                    const b = computeDomicileBodies(v, includeOtherMgmt, filterOptions.counselling_bodies || [], openStateBodies);
                    if (b) setSelectedBodies(b);
                  }
                }}>
                  <SelectTrigger className="border-zinc-300 focus:border-[#6043EF]" data-testid="domicile-state-select">
                    <SelectValue placeholder="Domicile state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="All India">All India (no domicile filter)</SelectItem>
                    {filterOptions.states.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {domicileState !== "All India" ? (
                  <>
                    <p className="text-[10px] text-[#52525B] mt-1.5 font-sans leading-relaxed">
                      Showing All-India-Quota (MCC) seats everywhere + <span className="font-bold text-[#6043EF]">{domicileState}</span> counselling.
                    </p>
                    <label className="flex items-start gap-2 mt-2 cursor-pointer" data-testid="include-other-mgmt-checkbox">
                      <input
                        type="checkbox"
                        checked={includeOtherMgmt}
                        onChange={(e) => {
                          setIncludeOtherMgmt(e.target.checked);
                          if (e.target.checked) { try { localStorage.removeItem("open_state_info_dismissed"); } catch { /* noop */ } setOpenInfoDismissed(false); }
                          const b = computeDomicileBodies(domicileState, e.target.checked, filterOptions.counselling_bodies || [], openStateBodies);
                          if (b) setSelectedBodies(b);
                        }}
                        className="w-3.5 h-3.5 mt-0.5 accent-[#6043EF]"
                      />
                      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#52525B] leading-snug flex items-center gap-1 flex-wrap">
                        Also include open states
                        <Star size={11} weight="fill" className="text-[#F59E0B] inline shrink-0" />
                        management / private seats
                        <button type="button" onClick={(e) => { e.preventDefault(); setOpenInfoDismissed(false); }}
                          className="text-[#6043EF] hover:text-[#4f35d4]" data-testid="open-state-info-icon" aria-label="What does this do?">
                          <Info size={13} weight="fill" className="inline" />
                        </button>
                      </span>
                    </label>
                  </>
                ) : (
                  <p className="text-[10px] text-zinc-400 mt-1.5 font-sans">
                    Pick your state to see only the seats you're eligible for.
                  </p>
                )}
              </div>

              {/* Open-states info popup — modal so the user is clearly notified */}
              {includeOtherMgmt && !openInfoDismissed && domicileState !== "All India" && (
                <div
                  className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px] animate-fade-in"
                  data-testid="open-state-info-modal"
                  onClick={() => { try { localStorage.setItem("open_state_info_dismissed", "1"); } catch { /* noop */ } setOpenInfoDismissed(true); }}
                >
                  <div
                    className="relative w-full max-w-[440px] rounded-2xl border border-[#6043EF]/30 bg-[#F5F3FF] shadow-2xl p-5 pr-10 animate-modal-pop"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => { try { localStorage.setItem("open_state_info_dismissed", "1"); } catch { /* noop */ } setOpenInfoDismissed(true); }}
                      className="absolute top-3.5 right-3.5 text-[#6043EF]/60 hover:text-[#6043EF]"
                      data-testid="open-state-info-dismiss" aria-label="Dismiss"
                    >
                      <X size={18} weight="bold" />
                    </button>
                    <p className="text-[15px] text-[#3F3F46] leading-relaxed font-sans">
                      <span className="font-bold text-[#6043EF]">{domicileState}</span> (home state): all categories are selectable.
                      For <span className="font-bold text-[#18181B]">open states</span> only the defined open / management / NRI seats are shown — that's the pool a non-domicile candidate can actually apply to.
                    </p>
                  </div>
                </div>
              )}


              {/* Counselling Body */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">COUNSELLING BODY</label>
                <div className="space-y-1" data-testid="counselling-body-checkboxes">
                  {(filterOptions.counselling_bodies || []).filter(b => {
                    return allBodiesGrouped[b] && allBodiesGrouped[b].length > 0;
                  }).map((b) => {
                    const domicileGate = domicileState !== "All India";
                    const homeBodies = STATE_BODY_MAP[domicileState] || [];
                    const isHomeOrMcc = b === "MCC" || homeBodies.includes(b);
                    const isOpenBody = openStateBodies.includes(b) && !homeBodies.includes(b);
                    let enabled = true, isOpenStar = false, restrictLabel = "";
                    if (domicileGate) {
                      if (isHomeOrMcc) {
                        enabled = true;
                      } else if (isOpenBody) {
                        isOpenStar = true;
                        enabled = includeOtherMgmt;          // unlocked only via mgmt checkbox
                        if (!enabled) restrictLabel = "(tick management quota)";
                      } else {
                        enabled = false;
                        restrictLabel = "(restricted for non-domicile)";
                      }
                    }
                    const isRestricted = !enabled;
                    return (
                    <label key={b} className={`flex items-center gap-2 group ${isRestricted ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`} data-testid={`body-checkbox-${b}`}>
                      <input
                        type="checkbox"
                        checked={selectedBodies.includes(b)}
                        disabled={isRestricted}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBodies(prev => [...prev, b]);
                          } else {
                            setSelectedBodies(prev => prev.filter(x => x !== b));
                          }
                        }}
                        className="w-3.5 h-3.5 accent-[#6043EF] disabled:cursor-not-allowed"
                      />
                      <span className={`text-xs font-['IBM_Plex_Mono'] font-medium transition-colors flex items-center gap-1 ${isRestricted ? "text-zinc-400" : "text-[#09090B] group-hover:text-[#6043EF]"}`}>
                        {b}
                        {isOpenStar && <Star size={11} weight="fill" className="text-[#F59E0B]" title="Open state — admits non-domicile (management/private)" />}
                        {restrictLabel && <span className="text-[9px] text-zinc-400 font-sans">{restrictLabel}</span>}
                      </span>
                    </label>
                    );
                  })}
                </div>
              </div>

              {/* Category — multi-select with tags, grouped by body */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">CATEGORY</label>
                {/* Select all toggle */}
                {Object.keys(effectiveGrouped).length > 0 && (() => {
                  const allCats = Array.from(new Set(Object.values(effectiveGrouped).flat()));
                  const isAll = allCats.length > 0 && allCats.every(c => selectedCategories.includes(c));
                  return (
                    <label className="flex items-center gap-2 mb-2 cursor-pointer group" data-testid="select-all-categories">
                      <input
                        type="checkbox"
                        checked={isAll}
                        onChange={(e) => setSelectedCategories(e.target.checked ? allCats : [])}
                        className="w-3.5 h-3.5 accent-[#6043EF]"
                      />
                      <span className="text-xs font-['IBM_Plex_Mono'] font-semibold text-[#6043EF] group-hover:underline">
                        Select all categories ({allCats.length})
                      </span>
                    </label>
                  );
                })()}
                {/* Selected tags with body prefix (hidden when ALL selected — implicit) */}
                {(() => {
                  const _all = Array.from(new Set(Object.values(effectiveGrouped).flat()));
                  const _isAll = _all.length > 0 && _all.every(c => selectedCategories.includes(c));
                  if (_isAll) {
                    return (
                      <div className="mb-1.5 inline-flex items-center gap-1 h-6 px-2 bg-[#6043EF] text-white text-[10px] font-['IBM_Plex_Mono'] font-bold rounded" data-testid="all-categories-summary">
                        ✓ All {_all.length} categories selected
                      </div>
                    );
                  }
                  if (effectiveSelectedCount === 0) return null;
                  return (
                    <div className="flex flex-wrap gap-1 mb-1.5" data-testid="selected-categories">
                      {Object.entries(bodyCategoriesMap).flatMap(([body, cats]) =>
                        cats.map((cat) => (
                          <span key={`${body}_${cat}`} className="inline-flex items-center gap-1 h-6 px-2 bg-[#6043EF] text-white text-[10px] font-['IBM_Plex_Mono'] font-bold">
                            {body}: {cat}
                            <button onClick={() => setSelectedCategories(prev => prev.filter(c => c !== cat))}
                              className="hover:text-red-200 ml-0.5" data-testid={`remove-cat-${body}-${cat}`}>&times;</button>
                          </span>
                        ))
                      )}
                    </div>
                  );
                })()}
                {/* Multi-select checkbox dropdown (stays open) */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white hover:border-[#6043EF] transition-colors"
                      data-testid="filter-category-trigger"
                    >
                      <span className="text-zinc-600">
                        {effectiveSelectedCount > 0 ? `${effectiveSelectedCount} selected` : "Select categories..."}
                      </span>
                      <CaretDown size={14} className="text-zinc-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" data-testid="category-popover">
                    <div className="max-h-72 overflow-y-auto overscroll-contain">
                      <div className="p-1.5">
                        {Object.entries(effectiveGrouped).map(([body, cats]) => {
                          const allBodySel = cats.every(c => selectedCategories.includes(c));
                          const bmap = categoryBuckets[body] || {};
                          // Order categories by fee bucket (NRI → Other → Private → Govt), then A–Z.
                          const orderedCats = [...cats].sort((a, b) => {
                            const ra = FEE_BUCKET_ORDER[bmap[a]] ?? 3;
                            const rb = FEE_BUCKET_ORDER[bmap[b]] ?? 3;
                            return ra - rb || a.localeCompare(b);
                          });
                          let lastBucket = null;
                          return (
                            <div key={body} className="mb-1">
                              <button
                                type="button"
                                onClick={() => setSelectedCategories(prev => allBodySel
                                  ? prev.filter(c => !cats.includes(c))
                                  : Array.from(new Set([...prev, ...cats])))}
                                className="w-full flex items-center justify-between px-2.5 py-2 rounded-md bg-[#2563EB] text-white hover:bg-[#1d4ed8] mb-1 sticky top-0 z-10 shadow-sm"
                                data-testid={`category-group-${body}`}
                              >
                                <span className="text-sm font-black uppercase tracking-[0.12em]">{body}</span>
                                <span className="text-[10px] font-bold normal-case tracking-normal text-white/85 hover:text-white">{allBodySel ? "Clear all" : "Select all"}</span>
                              </button>
                              {orderedCats.map((c) => {
                                const sel = selectedCategories.includes(c);
                                const bucket = bmap[c] || "govt";
                                const showLabel = bucket !== lastBucket;
                                lastBucket = bucket;
                                return (
                                  <div key={`${body}_${c}`}>
                                    {showLabel && (
                                      <div
                                        className={`flex items-center gap-1.5 px-2 pt-2 pb-0.5 text-[9px] font-bold uppercase tracking-[0.18em] ${FEE_BUCKET_META[bucket].text}`}
                                        data-testid={`category-bucket-${body}-${bucket}`}
                                      >
                                        <span className={`w-1.5 h-1.5 rounded-[1px] ${FEE_BUCKET_META[bucket].dot}`} />
                                        {FEE_BUCKET_META[bucket].label}
                                      </div>
                                    )}
                                    <label
                                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 cursor-pointer"
                                      data-testid={`category-option-${c}`}
                                    >
                                      <Checkbox
                                        checked={sel}
                                        onCheckedChange={() => setSelectedCategories(prev =>
                                          sel ? prev.filter(x => x !== c) : [...prev, c])}
                                        className="rounded-[3px]"
                                        data-testid={`category-check-${c}`}
                                      />
                                      <span className="text-sm font-['IBM_Plex_Mono']">{c}</span>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* College Type */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">COLLEGE TYPE</label>
                <Select value={collegeType} onValueChange={setCollegeType}>
                  <SelectTrigger className="border-zinc-300 focus:border-[#6043EF]" data-testid="filter-type-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="govt">Government</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="trust">Trust</SelectItem>
                    <SelectItem value="deemed">Deemed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* National Importance toggle */}
              <button
                type="button"
                onClick={() => setNationalImportanceOnly(v => !v)}
                aria-pressed={nationalImportanceOnly}
                className={`w-full h-9 flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wider border transition-colors duration-150 ${
                  nationalImportanceOnly
                    ? "bg-[#6043EF] text-white border-[#6043EF]"
                    : "bg-white text-[#52525B] border-zinc-300 hover:border-[#6043EF]"
                }`}
                data-testid="filter-national-importance">
                <SealCheck size={13} weight={nationalImportanceOnly ? "fill" : "bold"} />
                National Importance only
              </button>

              {/* Course */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">COURSE</label>
                <div className="flex flex-wrap gap-0" data-testid="course-pills">
                  {(() => {
                    // Derive available courses from selected bodies
                    const bodyCourses = new Set();
                    selectedBodies.forEach(b => {
                      (coursesGrouped[b] || []).forEach(c => bodyCourses.add(c));
                    });
                    // Fallback if nothing found
                    const courseList = bodyCourses.size > 0 ? [...bodyCourses].sort() : ["MBBS", "BDS"];
                    // Auto-select MBBS if current course not in list
                    if (!courseList.includes(course)) {
                      const fallback = courseList.includes("MBBS") ? "MBBS" : courseList[0];
                      setTimeout(() => setCourse(fallback), 0);
                    }
                    return courseList.map((c) => (
                      <button key={c} onClick={() => setCourse(c)}
                        className={`px-3 h-9 text-xs font-['IBM_Plex_Mono'] font-bold border transition-colors duration-150 ${
                          course === c
                            ? "bg-[#6043EF] text-white border-[#6043EF]"
                            : "bg-white text-[#52525B] border-zinc-300 hover:border-[#6043EF]"
                        }`}
                        data-testid={`course-pill-${c}`}>
                        {c}
                      </button>
                    ));
                  })()}
                </div>
              </div>

              {/* Counselling Round */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">ROUND</label>
                <Select value={roundNumber} onValueChange={setRoundNumber}>
                  <SelectTrigger className="border-zinc-300 focus:border-[#6043EF]" data-testid="filter-round-trigger"><SelectValue placeholder="All Rounds" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Rounds</SelectItem>
                    {[1, 2, 3, 4, 5, 6, 7].map((r) => (
                      <SelectItem key={r} value={String(r)}>Round {r}</SelectItem>
                    ))}
                    {[101, 102, 103].map((r) => (
                      <SelectItem key={r} value={String(r)}>Stray R{r - 100}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Max Fees */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">
                  MAX ANNUAL FEES: <span className="font-['IBM_Plex_Mono']">{formatFees(maxFees[0])}</span>
                </label>
                <Slider value={maxFees} onValueChange={setMaxFees} min={10000} max={5000000} step={50000} className="mt-2" data-testid="filter-fees-slider" />
                <div className="flex justify-between mt-1">
                  <span className="text-[10px] font-['IBM_Plex_Mono'] text-zinc-400">10K</span>
                  <span className="text-[10px] font-['IBM_Plex_Mono'] text-zinc-400">50L</span>
                </div>
              </div>

              {/* Historical Years */}
              {availableYears.length > 0 && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">HISTORICAL DATA</label>
                  <div className="flex flex-wrap gap-1.5">
                    {availableYears.map(yr => (
                      <button key={yr} onClick={() => toggleYear(yr)}
                        className={`h-7 px-3 text-xs font-['IBM_Plex_Mono'] font-bold border transition-colors duration-150 ${
                          selectedYears.includes(yr)
                            ? "bg-[#6043EF] text-white border-[#6043EF]"
                            : "bg-white text-[#52525B] border-zinc-300 hover:border-[#6043EF]"
                        }`}
                        data-testid={`year-pill-${yr}`}>
                        {yr}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Search Button */}
              <button onClick={handlePredict} disabled={!rank || loading || !categoriesReady}
                className="w-full h-10 bg-[#6043EF] text-white font-bold text-xs uppercase tracking-wider hover:bg-[#4A28C2] transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="predict-btn">
                {!categoriesReady ? (
                  <>
                    <CircleNotch size={14} weight="bold" className="animate-spin" />
                    UPDATING CATEGORIES…
                  </>
                ) : loading ? "PREDICTING..." : "PREDICT COLLEGES"}
                {categoriesReady && !loading && <ArrowRight size={12} weight="bold" />}
              </button>

              {/* AI Recommend — hidden for now */}
              {false && (
              <button onClick={handleAIRecommend} disabled={!rank || aiLoading || predictions.length === 0}
                className="w-full h-10 border border-[#6043EF] text-[#6043EF] font-bold text-xs uppercase tracking-wider hover:bg-[#6043EF] hover:text-white transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="ai-recommend-btn">
                <Sparkle size={12} weight="bold" />
                {aiLoading ? "ANALYZING..." : "AI RECOMMENDATION"}
              </button>
              )}
            </div>
          </div>

          {/* Results Panel */}
          <div className="col-span-12 lg:col-span-9" data-testid="results-panel">
            {/* AI Recommendation */}
            {aiRecommendation && (
              <div className="border border-[#6043EF] bg-[#6043EF]/5 p-4 mb-4 animate-fade-in" data-testid="ai-recommendation">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkle size={14} weight="bold" className="text-[#6043EF]" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF]">AI RECOMMENDATION</span>
                </div>
                <p className="text-sm font-sans leading-relaxed text-[#09090B]">{aiRecommendation}</p>
              </div>
            )}

            {/* Eligibility summary card (presented like the AI recommendation) */}
            {searched && !loading && total > 0 && (() => {
              const eAiq = predictions.filter(p => p.counselling_body === "MCC").length;
              const isDom = domicileState !== "All India";
              const eHome = isDom ? predictions.filter(p => p.counselling_body !== "MCC" && p.state === domicileState).length : 0;
              const eMgmt = isDom ? predictions.filter(p => p.counselling_body !== "MCC" && p.state !== domicileState).length : 0;
              const eState = !isDom ? total - eAiq : 0;
              return (
                <div className="border border-[#6043EF] bg-[#6043EF]/5 p-4 mb-4 animate-fade-in" data-testid="eligibility-summary">
                  <div className="flex items-center gap-2 mb-2">
                    <SealCheck size={14} weight="bold" className="text-[#6043EF]" />
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF]">YOUR ELIGIBILITY</span>
                  </div>
                  <p className="text-sm font-sans leading-relaxed text-[#09090B]">
                    {isDom ? (
                      <>As a <span className="font-bold text-[#6043EF]">{domicileState}</span> domicile at AIR <span className="font-bold">{Number(rank).toLocaleString()}</span>, you're eligible for <span className="font-bold">{total.toLocaleString()}</span> colleges — <span className="font-bold">{eAiq}</span> via All-India-Quota (MCC), <span className="font-bold">{eHome}</span> via {domicileState} state counselling{includeOtherMgmt && eMgmt > 0 ? <>, and <span className="font-bold">{eMgmt}</span> via open-state management/private</> : null}.</>
                    ) : (
                      <><span className="font-bold">{total.toLocaleString()}</span> colleges match AIR <span className="font-bold">{Number(rank).toLocaleString()}</span> — <span className="font-bold">{eAiq}</span> via All-India-Quota and <span className="font-bold">{eState}</span> via state counselling. <span className="text-[#52525B]">Set your domicile state for eligibility-accurate results.</span></>
                    )}
                  </p>
                </div>
              );
            })()}

            {/* Results Summary + State Filter */}
            {searched && (
              <div className="border border-zinc-200 p-4 mb-4 bg-[#F4F4F5] flex items-center justify-between flex-wrap gap-2" data-testid="results-summary">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">RESULTS FOR RANK </span>
                  <span className="font-['IBM_Plex_Mono'] font-bold text-[#09090B]">{rank}</span>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] ml-1">CATEGORY </span>
                  {(() => {
                    const allCats = Object.entries(bodyCategoriesMap).flatMap(([body, cats]) =>
                      cats.map((cat) => ({ body, cat })));
                    const LIMIT = 3;
                    const shown = catsExpanded ? allCats : allCats.slice(0, LIMIT);
                    const hidden = allCats.length - shown.length;
                    return (
                      <span className="inline-flex items-center gap-1 flex-wrap" data-testid="results-categories">
                        {shown.map(({ body, cat }) => (
                          <Badge key={`${body}_${cat}`} className={`${CATEGORY_COLORS[cat] || "bg-zinc-600 text-white"} text-[10px] uppercase tracking-wider`}>
                            {body}: {cat}
                          </Badge>
                        ))}
                        {hidden > 0 && (
                          <button onClick={() => setCatsExpanded(true)} className="text-[10px] font-bold uppercase tracking-wider text-[#6043EF] hover:underline" data-testid="categories-more-btn">
                            +{hidden} more
                          </button>
                        )}
                        {catsExpanded && allCats.length > LIMIT && (
                          <button onClick={() => setCatsExpanded(false)} className="text-[10px] font-bold uppercase tracking-wider text-[#6043EF] hover:underline" data-testid="categories-less-btn">
                            show less
                          </button>
                        )}
                      </span>
                    );
                  })()}
                  <span className="text-zinc-300 mx-1">|</span>
                  <Select value={filterState} onValueChange={(v) => { setFilterState(v); setTimeout(() => { document.querySelector('[data-testid="predict-btn"]')?.click(); }, 100); }}>
                    <SelectTrigger className="w-40 h-7 border-zinc-300 text-xs bg-white" data-testid="result-state-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="All">All States</SelectItem>
                      {filterOptions.states.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {filterState !== "All" && (
                    <button onClick={() => { setFilterState("All"); setTimeout(() => { document.querySelector('[data-testid="predict-btn"]')?.click(); }, 100); }} className="text-[10px] text-[#6043EF] font-bold uppercase hover:underline">CLEAR</button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-zinc-300 bg-white" data-testid="font-toggle">
                    <button
                      onClick={() => setFontLg(false)}
                      className={`h-7 w-7 flex items-center justify-center leading-none transition-colors ${!fontLg ? "bg-[#6043EF] text-white" : "text-[#52525B] hover:text-[#6043EF]"}`}
                      title="Normal text size" data-testid="font-sm-btn">
                      <span className="text-[11px] font-bold">A</span>
                    </button>
                    <button
                      onClick={() => setFontLg(true)}
                      className={`h-7 w-7 flex items-center justify-center leading-none transition-colors ${fontLg ? "bg-[#6043EF] text-white" : "text-[#52525B] hover:text-[#6043EF]"}`}
                      title="Larger text size" data-testid="font-lg-btn">
                      <span className="text-[15px] font-bold">A</span>
                    </button>
                  </div>
                  <div className="flex items-center border border-zinc-300 bg-white p-0.5" data-testid="view-toggle">
                    <button
                      onClick={() => setViewMode("table")}
                      className={`h-6 w-7 flex items-center justify-center transition-colors ${viewMode === "table" ? "bg-[#6043EF] text-white" : "text-[#52525B] hover:text-[#6043EF]"}`}
                      title="Table view" data-testid="view-table-btn">
                      <List size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => setViewMode("cards")}
                      className={`h-6 w-7 flex items-center justify-center transition-colors ${viewMode === "cards" ? "bg-[#6043EF] text-white" : "text-[#52525B] hover:text-[#6043EF]"}`}
                      title="Card view" data-testid="view-cards-btn">
                      <SquaresFour size={14} weight="bold" />
                    </button>
                  </div>
                  <div className="relative">
                    <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                      value={resultSearch}
                      onChange={(e) => setResultSearch(e.target.value)}
                      placeholder="Search colleges..."
                      className="h-7 w-48 pl-7 pr-6 text-xs border-zinc-300 bg-white focus:border-[#6043EF]"
                      data-testid="result-search-input"
                    />
                    {resultSearch && (
                      <button
                        onClick={() => setResultSearch("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-[#6043EF]"
                        data-testid="result-search-clear">&times;</button>
                    )}
                  </div>
                  {selectedBodies.includes("MCC") && (mccQuotaData.all || []).length > 0 && (
                    <div className="relative">
                      <button
                        onClick={() => setMccQuotaOpen((o) => !o)}
                        className="h-7 px-3 text-xs border border-zinc-300 bg-white hover:border-[#6043EF] flex items-center gap-1.5 whitespace-nowrap"
                        data-testid="mcc-quota-dropdown-btn"
                      >
                        MCC Quotas{mccQuotaSel.length > 0 ? ` (${mccQuotaSel.length})` : ""}
                        <span className="text-zinc-400">▾</span>
                      </button>
                      {mccQuotaOpen && (
                        <>
                          <div className="fixed inset-0 z-20" onClick={() => setMccQuotaOpen(false)} />
                          <div className="absolute right-0 mt-1 w-80 bg-white border border-zinc-200 shadow-lg z-30 p-2 max-h-[70vh] overflow-y-auto" data-testid="mcc-quota-panel">
                            <div className="flex items-center justify-between px-1 pb-1.5">
                              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Add quotas (personal eligibility)</p>
                              {mccQuotaSel.length > 0 && (
                                <button onClick={() => setMccQuotaSel([])} className="text-[10px] text-[#6043EF] hover:underline" data-testid="mcc-quota-clear">Clear</button>
                              )}
                            </div>
                            {mccSelectableQuotas.map((q) => (
                              <label key={q} className="flex items-start gap-2 px-1 py-1.5 text-xs cursor-pointer hover:bg-zinc-50" data-testid={`mcc-quota-opt-${q}`}>
                                <input
                                  type="checkbox"
                                  checked={mccQuotaSel.includes(q)}
                                  onChange={(e) => setMccQuotaSel((prev) => e.target.checked ? [...prev, q] : prev.filter((x) => x !== q))}
                                  className="accent-[#6043EF] mt-0.5"
                                />
                                <span className="leading-snug">{q}</span>
                              </label>
                            ))}
                            <div className="mt-2 pt-1.5 border-t border-zinc-100">
                              <p className="text-[10px] uppercase tracking-wider text-zinc-500 px-1 pb-1">Auto-applied by domicile</p>
                              {mccAutoInfo.map((it, i) => (
                                <div key={i} className={`flex items-start justify-between gap-2 px-1 py-1 text-xs ${it.active ? "text-[#3F3F46]" : "text-zinc-400"}`} data-testid={`mcc-quota-info-${i}`}>
                                  <span className="leading-snug flex items-start gap-1">
                                    {it.active && <span className="text-emerald-500">✓</span>}
                                    {it.q}
                                  </span>
                                  <span className={`shrink-0 text-[9px] px-1.5 py-0.5 rounded ${it.active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{it.tag}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <span className="font-['IBM_Plex_Mono'] text-sm text-[#52525B] whitespace-nowrap" data-testid="results-count">{filteredPredictions.length} colleges</span>
                </div>
              </div>
            )}

            {/* Loading Skeleton */}
            {loading && (
              <div className="space-y-2">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-12 bg-zinc-200 animate-pulse" />
                ))}
              </div>
            )}

            {/* Results Table grouped by state */}
            {!loading && viewMode === "table" && filteredPredictions.length > 0 && (
              <div className="border border-zinc-200 overflow-x-auto" data-testid="predictions-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                      <TableHead className={`font-bold ${fs.md} uppercase tracking-[0.15em] text-[#09090B] cursor-pointer min-w-[240px] sticky left-0 bg-[#F4F4F5] z-10`} onClick={() => handleSort("name")}>
                        <span className="flex items-center gap-1">COLLEGE <SortIcon field="name" /></span>
                      </TableHead>
                      <TableHead className={`font-bold ${fs.md} uppercase tracking-[0.15em] text-[#09090B]`}>TYPE</TableHead>
                      <TableHead className={`font-bold ${fs.md} uppercase tracking-[0.15em] text-[#09090B]`}>BODY</TableHead>
                      <TableHead className={`font-bold ${fs.md} uppercase tracking-[0.15em] text-[#09090B]`}>CAT</TableHead>
                      <TableHead className={`font-bold ${fs.md} uppercase tracking-[0.15em] text-[#09090B] text-right cursor-pointer`} onClick={() => handleSort("rank")}>
                        <span className="flex items-center justify-end gap-1">CLOSING RANK <SortIcon field="rank" /></span>
                      </TableHead>
                      <TableHead className={`font-bold ${fs.md} uppercase tracking-[0.15em] text-[#09090B] text-right cursor-pointer`} onClick={() => handleSort("fees")}>
                        <span className="flex items-center justify-end gap-1">FEES/YR <SortIcon field="fees" /></span>
                      </TableHead>
                      <TableHead className={`font-bold ${fs.md} uppercase tracking-[0.15em] text-[#09090B]`}>YR</TableHead>
                      <TableHead className={`font-bold ${fs.md} uppercase tracking-[0.15em] text-[#09090B]`}>RND</TableHead>
                      <TableHead className={`font-bold ${fs.md} uppercase tracking-[0.15em] text-[#09090B] cursor-pointer`} onClick={() => handleSort("chance")}>
                        <span className="flex items-center gap-1">CHANCE <SortIcon field="chance" /></span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {favoriteRows.length > 0 && (
                      <>
                        <TableRow className="bg-[#6043EF]/5 hover:bg-[#6043EF]/5" data-testid="favorites-group">
                          <TableCell colSpan={9} className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              <Heart size={13} weight="fill" className="text-[#EF4444]" />
                              <span className={`${fs.gt} font-bold uppercase tracking-[0.15em] text-[#09090B]`}>FAVORITES</span>
                              <span className={`font-['IBM_Plex_Mono'] ${fs.xs2} text-zinc-400`}>({favoriteRows.length})</span>
                            </div>
                          </TableCell>
                        </TableRow>
                        {favoriteRows.map((p, i) => (
                          <PredictionRow key={`fav-${p.college_id}-${i}`} p={p} i={`fav-${i}`} domicileState={domicileState}
                            isFavorite={true} onToggleFavorite={toggleFavorite} fs={fs} />
                        ))}
                      </>
                    )}
                    {filterState !== "All" ? (
                      // No grouping when filtered to single state
                      nonFavoriteRows.map((p, i) => <PredictionRow key={`${p.college_id}-${i}`} p={p} i={i} domicileState={domicileState}
                        isFavorite={favSet.has(p.college_id)} onToggleFavorite={toggleFavorite} fs={fs} />)
                    ) : (
                      // Grouped by state
                      orderedStates.map(state => (
                        <StateGroup key={state} state={state} colleges={groupedByState[state]}
                          domicileState={domicileState}
                          adjacentStates={domicileState !== "All India" ? (STATE_ADJACENCY[domicileState] || []) : []}
                          favSet={favSet} onToggleFavorite={toggleFavorite} fs={fs}
                          startIdx={(() => { const idx = globalIdx; globalIdx += groupedByState[state].length; return idx; })()} />
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Results Card grid grouped by state */}
            {!loading && viewMode === "cards" && filteredPredictions.length > 0 && (
              <div className="space-y-6" data-testid="predictions-cards">
                {favoriteRows.length > 0 && (
                  <CardSection title="FAVORITES" count={favoriteRows.length} rows={favoriteRows} isFav
                    domicileState={domicileState} favSet={favSet} onToggleFavorite={toggleFavorite} fs={fs} cardTexture={cardTexture} />
                )}
                {filterState !== "All" ? (
                  <CardSection title={filterState} count={nonFavoriteRows.length} rows={nonFavoriteRows} hideHeader
                    domicileState={domicileState} favSet={favSet} onToggleFavorite={toggleFavorite} fs={fs} cardTexture={cardTexture} />
                ) : (
                  orderedStates.map(state => (
                    <CardSection key={state} title={state} count={groupedByState[state].length} rows={groupedByState[state]}
                      domicileState={domicileState}
                      adjacentStates={domicileState !== "All India" ? (STATE_ADJACENCY[domicileState] || []) : []}
                      favSet={favSet} onToggleFavorite={toggleFavorite} fs={fs} cardTexture={cardTexture} />
                  ))
                )}
              </div>
            )}

            {/* No search match */}
            {!loading && predictions.length > 0 && filteredPredictions.length === 0 && (
              <div className="border border-zinc-200 border-dashed p-8 text-center mt-2" data-testid="no-search-match">
                <MagnifyingGlass size={32} className="text-zinc-300 mx-auto mb-3" />
                <p className="text-sm text-[#52525B] font-sans">No colleges match "<span className="font-bold">{resultSearch}</span>". Try a different search.</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && searched && predictions.length === 0 && (
              effectiveSelectedCount === 0 ? (
                <div className="border border-[#6043EF]/30 bg-[#6043EF]/[0.04] p-12 text-center" data-testid="empty-no-category">
                  <Funnel size={40} className="text-[#6043EF]/50 mx-auto mb-4" />
                  <p className="text-sm font-bold text-[#18181B] font-sans mb-1">Pick at least one category to see colleges</p>
                  <p className="text-xs text-[#52525B] font-sans">Choose a category from the <span className="font-semibold">CATEGORY</span> filter (e.g. your quota) and we'll show the seats you're eligible for.</p>
                </div>
              ) : (
                <div className="border border-zinc-200 p-12 text-center" data-testid="empty-results">
                  <Buildings size={40} className="text-zinc-300 mx-auto mb-4" />
                  <p className="text-sm text-[#52525B] font-sans">No colleges found for the given criteria. Try adjusting your filters or selecting more years.</p>
                </div>
              )
            )}

            {/* Initial State */}
            {!searched && !loading && (
              <div className="border border-zinc-200 border-dashed p-12 text-center" data-testid="initial-state">
                <MagnifyingGlass size={40} className="text-zinc-300 mx-auto mb-4" />
                <p className="text-sm text-[#52525B] font-sans">Enter your NEET rank and click Predict to see eligible colleges.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CardSection({ title, count, rows, domicileState, adjacentStates = [], favSet, onToggleFavorite, isFav, hideHeader, fs, cardTexture }) {
  const isHome = title === domicileState;
  const isAdjacent = adjacentStates.includes(title);
  const label = isFav ? "" : (isHome ? "HOME STATE" : isAdjacent ? "ADJACENT" : "");
  return (
    <div data-testid={`card-section-${title}`}>
      {!hideHeader && (
        <div className="flex items-center gap-2 mb-3 pb-1.5 border-b border-zinc-200">
          {isFav && <Heart size={13} weight="fill" className="text-[#EF4444]" />}
          <span className={`${fs.gt} font-bold uppercase tracking-[0.15em] text-[#09090B]`}>{title}</span>
          <span className={`font-['IBM_Plex_Mono'] ${fs.xs2} text-zinc-400`}>({count})</span>
          {label && (
            <span className={`${fs.s9} font-bold uppercase tracking-wider px-1.5 py-0.5 ${
              isHome ? "bg-[#6043EF] text-white" : "bg-zinc-200 text-[#52525B]"
            }`}>{label}</span>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {rows.map((p, i) => (
          <PredictionCard key={`${p.college_id}-${i}`} p={p} domicileState={domicileState}
            isFavorite={isFav || favSet?.has(p.college_id)} onToggleFavorite={onToggleFavorite} fs={fs} cardTexture={cardTexture} />
        ))}
      </div>
    </div>
  );
}

function PredictionCard({ p, domicileState, isFavorite, onToggleFavorite, fs, cardTexture }) {
  const elig = eligibilityTag(p, domicileState);
  const special = isSpecialMccQuota(p);
  const accent = p.chance === "high" ? "bg-[#16A34A]" : p.chance === "medium" ? "bg-[#F59E0B]" : "bg-[#EF4444]";
  return (
    <div className={`group relative border ${special ? "border-[#8B5CF6]/60" : "border-zinc-200"} bg-white flex flex-col hover:border-[#6043EF] hover:shadow-[0_4px_20px_rgba(96,67,239,0.10)] transition-all duration-150 overflow-hidden`} data-testid={`prediction-card-${p.college_id}`}>
      <div className={`h-1 w-full ${accent}`} />
      {/* Header (optionally textured) */}
      <div className="relative overflow-hidden">
        {cardTexture && (
          <>
            <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: `url(${cardTexture.url})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.9 }} aria-hidden="true" />
            <div className={`pointer-events-none absolute inset-0 ${cardTexture.mode === "light" ? "bg-black/25" : "bg-white/30"}`} aria-hidden="true" />
          </>
        )}
        <div className="relative flex items-start gap-2.5 p-4 pb-3">
          <CollegeLogo code={p.logo_code || p.college_id} hasLogo={!!p.has_logo} name={p.college_name} size={10} />
          <div className="flex-1 min-w-0">
            <Link to={`/colleges/${p.seo_path || p.college_id}`} title={p.college_name}
              className={`block font-semibold ${fs.big} hover:text-[#6043EF] transition-colors leading-snug line-clamp-2 ${cardTexture ? "" : "text-[#09090B]"}`}
              style={cardTexture ? { color: cardTexture.color, textShadow: cardTexture.shadow } : undefined}
              data-testid={`card-college-link-${p.college_id}`}>
              {p.college_name}
            </Link>
            <span className={`block ${fs.cityCard} font-sans mt-0.5 truncate ${cardTexture ? "" : "text-zinc-400"}`}
              style={cardTexture ? { color: cardTexture.color, textShadow: cardTexture.shadow, opacity: 0.85 } : undefined}>
              {p.city}{p.state ? `, ${p.state}` : ""}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onToggleFavorite?.(p.college_id)}
            className="shrink-0 transition-transform duration-150 hover:scale-125"
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
            aria-pressed={isFavorite}
            data-testid={`card-favorite-toggle-${p.college_id}`}>
            <Heart size={17} weight={isFavorite ? "fill" : "regular"} className={isFavorite ? "text-[#EF4444]" : "text-zinc-300 hover:text-[#EF4444]"} />
          </button>
        </div>
      </div>
      <div className="p-4 pt-3 flex flex-col gap-3 flex-1">

        {/* Highlight badges */}
        {(p.national_importance || p.rank_highlight) && (
          <div className="flex flex-wrap gap-1">
            {p.national_importance && (
              <span title="Institute of National Importance" className={`inline-flex items-center gap-0.5 bg-[#6043EF] text-white ${fs.tiny} font-bold uppercase tracking-wider px-1 py-0.5`}>
                <SealCheck size={9} weight="fill" /> INI
              </span>
            )}
            {p.rank_highlight && (
              <span title={p.rank_highlight} className={`inline-flex items-center gap-0.5 border border-[#92700C] text-[#92700C] ${fs.tiny} font-bold uppercase tracking-wider px-1 py-0.5`}>
                <Trophy size={9} weight="fill" /> {p.rank_highlight}
              </span>
            )}
          </div>
        )}

        {/* Meta badges */}
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className={`${fs.md} uppercase tracking-wider ${TYPE_BADGE[p.college_type] || "border-zinc-300"}`}>{p.college_type}</Badge>
          {p.is_deemed && (
            <Badge variant="outline" className={`${fs.md} uppercase tracking-wider bg-[#6043EF] text-white border-[#6043EF]`} title="Deemed-to-be University status">Deemed</Badge>
          )}
          <span className={`inline-flex items-center gap-1 ${fs.md} text-[#52525B] font-['IBM_Plex_Mono']`}>
            {p.counselling_body}
            {!special && (
              <span className={`${fs.tiny} font-bold uppercase tracking-wider px-1 py-0.5 ${elig.cls}`} title={elig.title}>{elig.label}</span>
            )}
          </span>
          <Badge className={`${CATEGORY_COLORS[p.category] || "bg-zinc-100 text-zinc-600 border border-zinc-300"} ${fs.md} uppercase tracking-wider`}>{p.category}</Badge>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 border-t border-zinc-100 pt-3 mt-auto">
          <div>
            <p className={`${fs.s9} uppercase tracking-wider text-zinc-400 font-bold mb-0.5`}>Closing Rank</p>
            <p className={`font-['IBM_Plex_Mono'] font-bold ${fs.big} text-[#09090B]`}>{p.last_year_closing_rank.toLocaleString()}</p>
            {p.min_rank && p.max_rank && p.min_rank !== p.max_rank && (
              <p className={`${fs.s9} font-['IBM_Plex_Mono'] text-zinc-400`}>
                {p.min_rank.toLocaleString()}–{p.max_rank.toLocaleString()}
                {p.trend === "tightening" && " ↓"}
                {p.trend === "relaxing" && " ↑"}
              </p>
            )}
            {p.rank_basis === "keam_proxy" && (
              <p className={`${fs.s9} font-bold text-amber-600`} data-testid={`rank-basis-proxy-${p.college_id}`}
                 title="Shown against the KEAM state merit rank — the true NEET AIR appears once this state's merit/rank list is ingested.">
                KEAM proxy · AIR pending
              </p>
            )}
            {p.rank_basis === "air" && ["Kerala", "Jharkhand"].includes(p.counselling_body) && (
              <p className={`${fs.s9} font-bold text-emerald-600`} data-testid={`rank-basis-air-${p.college_id}`}
                 title="Based on the true NEET All-India Rank from the state merit list.">
                true NEET AIR
              </p>
            )}
          </div>
          <div>
            <p className={`${fs.s9} uppercase tracking-wider text-zinc-400 font-bold mb-0.5`}>Fees/Yr</p>
            <div className={`font-['IBM_Plex_Mono'] ${fs.big} text-[#52525B]`}><FeeDisplay p={p} align="start" /></div>
          </div>
          <div className="text-right">
            <p className={`${fs.s9} uppercase tracking-wider text-zinc-400 font-bold mb-0.5`}>Chance</p>
            <ChanceBadge chance={p.chance} optimistic={p.optimistic} sizeClass={fs.md} testid={`chance-badge-card-${p.college_id}`} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-zinc-100 pt-2">
          <span className={`inline-flex items-center ${fs.md} font-['IBM_Plex_Mono'] font-semibold px-1.5 py-0.5 rounded ${yearChip(p.year)}`}>Year {p.year}</span>
          <span className={`inline-flex items-center ${fs.md} font-['IBM_Plex_Mono'] font-semibold px-1.5 py-0.5 rounded ${roundChip(p.round_number)}`}>{formatRound(p.round_number)}</span>
        </div>
      </div>
      {special && (
        <div className={`flex items-center gap-1.5 px-3 py-0.5 ${elig.cls}`} title={`${p.quota} — verify your eligibility`} data-testid={`card-quota-strip-${p.college_id}`}>
          <SealCheck size={11} weight="fill" className="shrink-0" />
          <span className={`${fs.tiny} font-bold uppercase tracking-wide truncate`}>{p.quota}</span>
          <span className={`${fs.tiny} opacity-80 ml-auto shrink-0`}>verify you qualify</span>
        </div>
      )}
    </div>
  );
}


function StateGroup({ state, colleges, domicileState, adjacentStates, startIdx, favSet, onToggleFavorite, fs }) {
  const isHome = state === domicileState;
  const isAdjacent = adjacentStates.includes(state);
  const label = isHome ? "HOME STATE" : isAdjacent ? "ADJACENT" : "";

  return (
    <>
      <TableRow className="bg-zinc-50 hover:bg-zinc-50" data-testid={`state-group-${state}`}>
        <TableCell colSpan={9} className="py-2 px-3">
          <div className="flex items-center gap-2">
            <span className={`${fs.gt} font-bold uppercase tracking-[0.15em] text-[#09090B]`}>{state}</span>
            <span className={`font-['IBM_Plex_Mono'] ${fs.xs2} text-zinc-400`}>({colleges.length})</span>
            {label && (
              <span className={`${fs.s9} font-bold uppercase tracking-wider px-1.5 py-0.5 ${
                isHome ? "bg-[#6043EF] text-white" : "bg-zinc-200 text-[#52525B]"
              }`}>{label}</span>
            )}
          </div>
        </TableCell>
      </TableRow>
      {colleges.map((p, i) => (
        <PredictionRow key={`${p.college_id}-${startIdx + i}`} p={p} i={startIdx + i} domicileState={domicileState}
          isFavorite={favSet?.has(p.college_id)} onToggleFavorite={onToggleFavorite} fs={fs} />
      ))}
    </>
  );
}

// MCC quotas open to every candidate (no personal eligibility gate).
// Mirrors the backend `universal` set from /api/mcc-quota-options.
const UNIVERSAL_MCC_QUOTAS = new Set([
  "All India", "B.Sc Nursing All India", "Open Seat Quota",
  "Deemed/Paid Seats Quota", "Non-Resident Indian", "Foreign Country Quota",
  "(AMU) Self Finance All India",
]);
// Auto-applied by domicile (Delhi/Puducherry) — mirrors backend `domicile_auto`.
const DOMICILE_MCC_QUOTAS = new Set([
  "B.Sc Nursing Delhi NCR", "B.Sc Nursing Delhi NCR CW Quota", "B.Sc Nursing IP CW Quota",
  "Delhi NCR Children/Widows of Personnel of the Armed Forces (CW) DU Quota",
  "Delhi NCR Children/Widows of Personnel of the Armed Forces (CW) IP Quota",
  "Delhi University Quota", "IP University Quota",
  "Internal - Puducherry UT Domicile",
]);
// Special = MCC seat under a PERSONAL-eligibility quota (minority, AMU, Jamia, ESI).
// Universal ("any domicile") and domicile-auto quotas are NOT special — they show as
// a coloured badge under MCC. Only these personal ones get the eyeball-verify strip.
function isSpecialMccQuota(p) {
  return p.counselling_body === "MCC" && !!p.quota
    && !UNIVERSAL_MCC_QUOTAS.has(p.quota) && !DOMICILE_MCC_QUOTAS.has(p.quota);
}

// Short label + tooltip for an MCC quota name.
function mccQuotaTag(quota) {
  const q = quota || "All India";
  const map = {
    "All India": ["AIQ", "All India Quota — open to all domiciles"],
    "B.Sc Nursing All India": ["AIQ", "All India Quota (B.Sc Nursing)"],
    "Open Seat Quota": ["OPEN", "Open Seat Quota (deemed) — open to all"],
    "Deemed/Paid Seats Quota": ["DEEMED", "Deemed university paid seat — open to all"],
    "Non-Resident Indian": ["NRI", "Non-Resident Indian quota"],
    "Foreign Country Quota": ["FOREIGN", "Foreign country quota"],
  };
  const label = q.length > 14 ? q.replace(/quota/i, "").trim().slice(0, 14) + "…" : q;
  const title = map[q] ? map[q][1] : q;
  // Cyan = open to any domicile; grey = auto-applied for a specific state/UT domicile.
  if (UNIVERSAL_MCC_QUOTAS.has(q)) return { label, title, cls: "bg-[#06B6D4] text-white" };
  if (DOMICILE_MCC_QUOTAS.has(q)) return { label, title: `${title} — auto-applied by domicile`, cls: "bg-zinc-300 text-[#3F3F46]" };
  // Personal-eligibility quotas (rendered as the verify strip).
  return { label, title, cls: "bg-[#8B5CF6] text-white" };
}

// "Eligible via" tag — explains why a college appears for this candidate.
function eligibilityTag(p, dom) {
  if (p.counselling_body === "MCC")
    return mccQuotaTag(p.quota);
  // Merged Govt+Mgmt bodies (e.g. Telangana) carry an explicit quota.
  if (p.quota === "MGMT")
    return { label: "MGMT", title: "Management / open quota", cls: "bg-[#F59E0B] text-black" };
  if (p.quota === "GOVT")
    return { label: "GOVT", title: "Govt / convener quota (domicile)", cls: "bg-[#00A37A] text-white" };
  if (dom && dom !== "All India" && p.state !== dom)
    return { label: "MGMT", title: "Management / open quota (open state, non-domicile)", cls: "bg-[#F59E0B] text-black" };
  return { label: "STATE", title: "State quota — state counselling", cls: "bg-zinc-200 text-[#52525B]" };
}

function PredictionRow({ p, i, domicileState, isFavorite, onToggleFavorite, fs }) {
  const elig = eligibilityTag(p, domicileState);
  const special = isSpecialMccQuota(p);
  const collegeCellRef = useRef(null);
  const [stripLeft, setStripLeft] = useState(240);
  useLayoutEffect(() => {
    if (!special) return;
    const measure = () => { if (collegeCellRef.current) setStripLeft(collegeCellRef.current.offsetWidth); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [special, p.college_name]);
  return (
    <TableRow className={`hover:bg-zinc-50 transition-colors duration-150 relative ${special ? "bg-[#8B5CF6]/[0.04] [&>td]:align-top [&>td]:pb-6" : ""}`} data-testid={`prediction-row-${i}`}>
      <TableCell ref={collegeCellRef} className={`font-medium ${fs.big} sticky left-0 bg-white z-10 border-r border-zinc-100`}>
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={() => onToggleFavorite?.(p.college_id)}
            className="mt-0.5 shrink-0 transition-transform duration-150 hover:scale-125"
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
            aria-pressed={isFavorite}
            data-testid={`favorite-toggle-${i}`}>
            <Heart size={16} weight={isFavorite ? "fill" : "regular"} className={isFavorite ? "text-[#EF4444]" : "text-zinc-300 hover:text-[#EF4444]"} />
          </button>
          <CollegeLogo code={p.logo_code || p.college_id} hasLogo={!!p.has_logo} name={p.college_name} />
          <div>
            <Link to={`/colleges/${p.seo_path || p.college_id}`} className="text-[#09090B] hover:text-[#6043EF] transition-colors" data-testid={`college-link-${i}`}>
              {p.college_name}
            </Link>
            <span className={`block ${fs.xs2} text-zinc-400 font-sans`}>{p.city}</span>
            {(p.national_importance || p.rank_highlight) && (
              <span className="flex items-center gap-1 mt-0.5 flex-wrap">
                {p.national_importance && (
                  <span title="Institute of National Importance" className={`inline-flex items-center gap-0.5 bg-[#6043EF] text-white ${fs.tiny} font-bold uppercase tracking-wider px-1 py-0.5`}>
                    <SealCheck size={9} weight="fill" /> INI
                  </span>
                )}
                {p.rank_highlight && (
                  <span title={p.rank_highlight} className={`inline-flex items-center gap-0.5 border border-[#92700C] text-[#92700C] ${fs.tiny} font-bold uppercase tracking-wider px-1 py-0.5`}>
                    <Trophy size={9} weight="fill" /> {p.rank_highlight}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={`${fs.md} uppercase tracking-wider border-zinc-300`}>{p.college_type}</Badge>
        {p.is_deemed && (
          <Badge variant="outline" className={`ml-1 ${fs.md} uppercase tracking-wider bg-[#6043EF] text-white border-[#6043EF]`} title="Deemed-to-be University status">Deemed</Badge>
        )}
        {special && (
          <div style={{ left: stripLeft }} className={`absolute right-0 bottom-0 flex items-center gap-1.5 px-2 py-0.5 ${elig.cls}`} title={`${p.quota} — verify your eligibility`} data-testid={`quota-strip-${i}`}>
            <SealCheck size={10} weight="fill" className="shrink-0" />
            <span className={`${fs.tiny} font-bold uppercase tracking-wide truncate`}>{p.quota}</span>
            <span className={`${fs.tiny} opacity-80 shrink-0`}>· verify you qualify</span>
          </div>
        )}
      </TableCell>
      <TableCell className={`${fs.sm2} text-[#52525B]`}>
        <span className="block">{p.counselling_body}</span>
        {!special && (
          <span className={`inline-block mt-0.5 ${fs.tiny} font-bold uppercase tracking-wider px-1 py-0.5 ${elig.cls}`} title={elig.title} data-testid={`elig-${i}`}>
            {elig.label}
          </span>
        )}
      </TableCell>
      <TableCell>
        <Badge className={`${CATEGORY_COLORS[p.category] || "bg-zinc-100 text-zinc-600 border border-zinc-300"} ${fs.md} uppercase tracking-wider`}>{p.category}</Badge>
      </TableCell>
      <TableCell className={`text-right font-['IBM_Plex_Mono'] font-semibold ${fs.big} text-[#09090B]`}>
        {p.last_year_closing_rank.toLocaleString()}
        {p.min_rank && p.max_rank && p.min_rank !== p.max_rank && (
          <span className={`block ${fs.xs2} font-normal text-zinc-400`}>
            {p.min_rank.toLocaleString()}–{p.max_rank.toLocaleString()}
            {p.year_range?.length > 1 && ` (${p.year_range.length}yr)`}
            {p.trend === "tightening" && " ↓"}
            {p.trend === "relaxing" && " ↑"}
          </span>
        )}
      </TableCell>
      <TableCell className={`text-right font-['IBM_Plex_Mono'] ${fs.big} text-[#52525B]`}>
        <FeeDisplay p={p} align="end" />
      </TableCell>
      <TableCell className={`font-['IBM_Plex_Mono'] ${fs.sm2} text-zinc-400`}>{p.year}</TableCell>
      <TableCell className={`font-['IBM_Plex_Mono'] ${fs.big} text-center`}>{formatRound(p.round_number)}</TableCell>
      <TableCell>
        <ChanceBadge chance={p.chance} optimistic={p.optimistic} sizeClass={fs.md} testid={`chance-badge-${p.college_id}`} />
      </TableCell>
    </TableRow>
  );
}
